import pdfMake from './pdf/setupPdfMake';

function countLeafColumns(col) {
    if (!col.children?.length) {
        return col.key ? 1 : 0;
    }
    return col.children.reduce((sum, child) => sum + countLeafColumns(child), 0);
}

// Recursively flatten columns to leaf columns (with key)
const flattenColumns = (columns) => {
    const result = [];
    columns.forEach((col) => {
        if (col.children?.length) {
            result.push(...flattenColumns(col.children));
        } else if (col.key) {
            result.push(col);
        }
    });
    return result;
};

function getColumnDepth(columns) {
    let maxDepth = 1;
    columns.forEach((col) => {
        if (col.children?.length) {
            maxDepth = Math.max(maxDepth, 1 + getColumnDepth(col.children));
        }
    });
    return maxDepth;
}

// Helper function to check if columns have grouping
const hasGroupedColumns = (columns) => {
    return columns.some(col => col.children && col.children.length > 0);
};

function headerCell(text, styles, options = {}) {
    return {
        text: String(text ?? ''),
        style: 'tableHeader',
        fillColor: styles.tableHeaderBgColor || '#28a745',
        color: styles.tableHeaderTextColor || '#ffffff',
        alignment: 'center',
        margin: [1, 2, 1, 2],
        ...options,
    };
}

function getTableHeaderRowHeight(styles, rowIndex, headerRows, hasGroupedHeaders) {
    if (rowIndex >= headerRows) return null;

    const parentHeight = parseInt(styles.tableHeaderRowHeight) || 20;
    const leafHeight = parseInt(styles.tableHeaderLeafRowHeight) || 32;

    if (headerRows === 1) return leafHeight;
    if (hasGroupedHeaders && rowIndex === headerRows - 1) return leafHeight;
    return parentHeight;
}

function resolveHeaderText(col, data) {
    if (col.labelKey && data.length > 0) {
        return data[0][col.labelKey] || col.label || col.key || '';
    }
    return col.label || col.key || '';
}

function buildGroupedTableHeaders(columns, depth, data, styles, rowNumberLabel) {
    const headerRows = Array.from({ length: depth }, () => []);

    const walk = (cols, level) => {
        cols.forEach((col) => {
            if (col.children?.length) {
                const leafCount = countLeafColumns(col);
                headerRows[level].push(headerCell(resolveHeaderText(col, data), styles, { colSpan: leafCount }));
                for (let i = 1; i < leafCount; i += 1) {
                    headerRows[level].push({});
                }
                walk(col.children, level + 1);
            } else {
                const rowSpan = depth - level;
                headerRows[level].push(headerCell(resolveHeaderText(col, data), styles, { rowSpan }));
                for (let nextLevel = level + 1; nextLevel < depth; nextLevel += 1) {
                    headerRows[nextLevel].push({});
                }
            }
        });
    };

    walk(columns, 0);

    headerRows[0].unshift(headerCell(rowNumberLabel, styles, { rowSpan: depth }));
    for (let level = 1; level < depth; level += 1) {
        headerRows[level].unshift({});
    }

    return headerRows;
}

function buildDataRow(item, rowNumber, flatColumns) {
    return [
        { text: String(rowNumber), alignment: 'center' },
        ...flatColumns.map(col => {
            let value;
            if (col.render) {
                value = col.render(item);
            } else {
                value = (item[col.key] !== undefined && item[col.key] !== null) ? item[col.key] : '-';
            }
            return String(value);
        }),
    ];
}

function buildGroupedTableBody(data, config, flatColumns, styles) {
    const groupField = config.groupRowsBy;
    const formatTitle = config.formatGroupTitle || ((value) => String(value ?? ''));
    const columnCount = 1 + flatColumns.length;
    const groups = new Map();
    const groupOrder = [];

    data.forEach((item) => {
        const key = item?.[groupField] ?? '';
        if (!groups.has(key)) {
            groups.set(key, []);
            groupOrder.push(key);
        }
        groups.get(key).push(item);
    });

    const sortedKeys = typeof config.sortGroupKeys === 'function'
        ? config.sortGroupKeys(groupOrder)
        : groupOrder;

    const body = [];
    let globalRowNumber = 0;

    sortedKeys.forEach((groupKey) => {
        const items = groups.get(groupKey) || [];
        if (items.length === 0) return;

        body.push([
            {
                text: formatTitle(groupKey),
                colSpan: columnCount,
                alignment: 'left',
                bold: true,
                fillColor: styles.sectionHeaderBgColor || '#e9ecef',
            },
            ...Array.from({ length: columnCount - 1 }, () => ({})),
        ]);

        items.forEach((item, index) => {
            const rowNumber = config.resetRowNumberPerGroup ? index + 1 : ++globalRowNumber;
            body.push(buildDataRow(item, rowNumber, flatColumns));
        });
    });

    return body;
}

function sumNumericWidths(widths) {
    if (!Array.isArray(widths) || widths.length === 0) return 0;
    if (!widths.every((width) => typeof width === 'number')) return 0;
    return widths.reduce((sum, width) => sum + width, 0);
}

function buildPostTableSignatureBlock(config, data) {
    const postTableLines = Array.isArray(config.postTableLines)
        ? config.postTableLines.filter(Boolean)
        : null;

    if (postTableLines?.length) {
        return {
            margin: [0, 16, 0, 0],
            alignment: 'right',
            columns: [
                {
                    stack: postTableLines.map((line, index) => ({
                        text: line,
                        fontSize: 8,
                        color: '#333',
                        margin: [0, index === 0 ? 0 : 2, 0, 0],
                    })),
                },
            ],
        };
    }

    return {
        margin: [0, 16, 0, 0],
        alignment: 'right',
        columns: [
            {
                stack: [
                    { text: 'Изготвил: . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . . .', fontSize: 8, color: '#333' },
                    { text: `/${String(config.preparedBy ?? config.customStyles?.preparedBy ?? data[0]?.fullName ?? '').toUpperCase().trim()}/`, fontSize: 8, color: '#333', margin: [0, 2, 0, 0] },
                ],
            },
        ],
    };
}

function resolvePageLayout(styles, config, tableWidths) {
    const pageMargins = styles.pageMargins || [30, 80, 30, 50];
    const tableWidth = sumNumericWidths(tableWidths);

    if (config.expandPageToFitTable && tableWidth > 0) {
        const pageHeight = styles.pageHeight || 841.89;
        return {
            pageSize: {
                width: tableWidth + pageMargins[0] + pageMargins[2],
                height: pageHeight,
            },
            pageOrientation: undefined,
            pageMargins,
        };
    }

    return {
        pageSize: styles.pageSize || 'A4',
        pageOrientation: styles.pageOrientation || 'landscape',
        pageMargins,
    };
}

export const generatePDFFromTemplate = async (data, config) => {
    try {
        const customStyles = config.customStyles || {};
        const styles = config.compactTable
            ? {
                pageSize: 'A3',
                pageOrientation: 'landscape',
                pageMargins: [8, 48, 8, 28],
                tableFontSize: '5',
                titleFontSize: '11',
                subtitleFontSize: '8',
                footerFontSize: '5',
                ...customStyles,
            }
            : customStyles;
        const now = new Date();
        const currentDate = now.toLocaleDateString('bg-BG');
        const genDateTime = `Дата: ${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()} г., час: ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
        
        // Check if data is valid
        if (!Array.isArray(data)) {
            throw new Error('Report data must be an array');
        }
        
        if (data.length === 0) {
            throw new Error('No data available for report');
        }

        let tableHeaders;
        let tableBody;
        let headerRows = 1;
        let tableWidths = ['auto'];
        let hasGrouping = false;

        if (typeof config.buildCustomTable === 'function') {
            const customTable = config.buildCustomTable(data, styles);
            tableHeaders = customTable.headers;
            tableBody = customTable.body;
            headerRows = customTable.headerRows ?? customTable.headers.length;
            tableWidths = customTable.widths ?? Array(customTable.columnCount || 1).fill('auto');
        } else {
        // Flatten columns to get all leaf columns
        const flatColumns = flattenColumns(config.columns);
        hasGrouping = hasGroupedColumns(config.columns);

        if (hasGrouping) {
            const depth = getColumnDepth(config.columns);
            const rowNumberLabel = config.rowNumberLabel ?? config.customStyles?.rowNumberLabel ?? '#';
            const labelHeaderRows = buildGroupedTableHeaders(
                config.columns,
                depth,
                data,
                styles,
                rowNumberLabel,
            );
            tableHeaders = labelHeaderRows;
            headerRows = depth;
        } else {
            const rowNumberLabel = config.rowNumberLabel ?? config.customStyles?.rowNumberLabel ?? '#';
            const headers = [rowNumberLabel, ...flatColumns.map(col => {
                return col.labelKey && data.length > 0
                    ? (data[0][col.labelKey] || col.label || col.key)
                    : (col.label || col.key);
            })];
            headerRows = 1;
            tableHeaders = [headers.map((header) => headerCell(header, styles))];
        }
        
        // Prepare table rows
        tableBody = config.groupRowsBy
            ? buildGroupedTableBody(data, config, flatColumns, styles)
            : data.map((item, index) => buildDataRow(item, index + 1, flatColumns));

        tableWidths = config.tableWidths ?? ['auto', ...Array(flatColumns.length).fill('auto')];
        }

        const preTableLines = Array.isArray(config.preTableLines)
            ? config.preTableLines.filter(Boolean)
            : [];

        const contentBlocks = [];

        const titleAlignment = config.titleAlignment || 'center';

        if (Array.isArray(config.titleLines) && config.titleLines.length) {
            contentBlocks.push({
                stack: config.titleLines.map((line, index) => ({
                    text: line,
                    alignment: titleAlignment,
                    bold: index === 0,
                    color: index === 0
                        ? (styles.titleColor || '#28a745')
                        : (styles.subtitleColor || '#555555'),
                    fontSize: index === 0
                        ? (parseInt(styles.titleFontSize) || 20)
                        : (parseInt(styles.subtitleFontSize) || 12),
                    margin: [
                        0,
                        index === 0 ? 4 : 2,
                        0,
                        index === config.titleLines.length - 1 ? 10 : 0,
                    ],
                })),
            });
        }

        contentBlocks.push(
            ...preTableLines.map((line) => ({
                text: line,
                alignment: 'left',
                margin: [0, 0, 0, 2],
                fontSize: parseInt(config.preTableFontSize) || 8,
                color: styles.footerColor || '#666666',
            })),
        );

        if (!config.titleLines?.length && config.hidePageHeader && config.title) {
            contentBlocks.push({
                stack: [
                    {
                        text: config.title,
                        alignment: titleAlignment,
                        bold: true,
                        color: styles.titleColor || '#28a745',
                        fontSize: parseInt(styles.titleFontSize) || 20,
                        margin: [0, 8, 0, 4],
                    },
                    ...(config.subtitle
                        ? [{
                            text: config.subtitle,
                            alignment: titleAlignment,
                            color: styles.subtitleColor || '#555555',
                            fontSize: parseInt(styles.subtitleFontSize) || 12,
                            margin: [0, 0, 0, 8],
                        }]
                        : []),
                ],
            });
        }

        contentBlocks.push(
            {
                text: genDateTime,
                margin: [0, 0, 0, 8],
                fontSize: 8,
                color: styles.footerColor || '#666666',
            },
            {
                table: {
                    headerRows: headerRows,
                    widths: tableWidths,
                    heights: (rowIndex) => getTableHeaderRowHeight(styles, rowIndex, headerRows, hasGrouping),
                    body: [
                        ...tableHeaders,
                        ...tableBody,
                    ],
                    dontBreakRows: true,
                },
                layout: {
                    paddingLeft: () => 2,
                    paddingRight: () => 2,
                    paddingTop: (rowIndex) => (rowIndex < headerRows ? 4 : 3),
                    paddingBottom: (rowIndex) => (rowIndex < headerRows ? 4 : 3),
                    fillColor: function (rowIndex, node, columnIndex) {
                        if (rowIndex < headerRows) return null;
                        return (rowIndex % 2 === 0)
                            ? (styles.tableRowEvenBgColor || '#f1f8f4')
                            : (styles.tableRowOddBgColor || '#ffffff');
                    },
                    hLineColor: function (i, node) {
                        return styles.tableCellBorderColor || '#dee2e6';
                    },
                    vLineColor: function (i, node) {
                        return styles.tableCellBorderColor || '#dee2e6';
                    },
                },
            },
            buildPostTableSignatureBlock(config, data),
        );

        const pageLayout = resolvePageLayout(styles, config, tableWidths);

        // PDF Document definition
        const docDefinition = {
            pageSize: pageLayout.pageSize,
            ...(pageLayout.pageOrientation ? { pageOrientation: pageLayout.pageOrientation } : {}),
            pageMargins: pageLayout.pageMargins,
            
            header: function(currentPage, pageCount) {
                if (config.hidePageHeader) {
                    return null;
                }
                // Show header only on first page
                if (currentPage === 1) {
                    const headerStack = [
                        {
                            text: config.title,
                            style: 'header',
                            alignment: 'center',
                            color: styles.titleColor || '#28a745',
                            fontSize: parseInt(styles.titleFontSize) || 20,
                        },
                    ];

                    if (config.subtitle) {
                        String(config.subtitle)
                            .split('\n')
                            .filter(Boolean)
                            .forEach((line) => {
                                headerStack.push({
                                    text: line,
                                    alignment: 'center',
                                    color: styles.subtitleColor || '#555555',
                                    fontSize: parseInt(styles.subtitleFontSize) || 12,
                                    margin: [0, 4, 0, 0],
                                });
                            });
                    }

                    if (config.companyName) {
                        headerStack.push({
                            text: config.companyName,
                            style: 'subheader',
                            alignment: 'center',
                            color: styles.subtitleColor || '#555555',
                            fontSize: parseInt(styles.subtitleFontSize) || 12,
                        });
                    }

                    return {
                        margin: [40, 20, 40, 0],
                        columns: [{ stack: headerStack }],
                    };
                }
                return null; // No header on other pages
            },
            
            footer: function(currentPage, pageCount) {
                return {
                    margin: [40, 10, 40, 10],
                    columns: [
                        {
                            text: `Генерирано на: ${currentDate}`,
                            fontSize: parseInt(styles.footerFontSize) || 8,
                            color: styles.footerColor || '#666666'
                        },
                        {
                            text: `Страница ${currentPage} от ${pageCount}`,
                            alignment: 'center',
                            fontSize: parseInt(styles.footerFontSize) || 8,
                            color: styles.footerColor || '#666666'
                        },
                        {
                            text: `Общо: ${data.length}`,
                            alignment: 'right',
                            fontSize: parseInt(styles.footerFontSize) || 8,
                            color: styles.footerColor || '#666666'
                        }
                    ]
                };
            },
            
            content: contentBlocks,
            
            styles: {
                header: {
                    bold: true,
                    margin: [0, 0, 0, 5]
                },
                subheader: {
                    margin: [0, 0, 0, 10]
                },
                tableHeader: {
                    bold: true,
                    fontSize: parseInt(styles.tableHeaderFontSize) || parseInt(styles.tableFontSize) || 10,
                    alignment: 'center',
                    lineHeight: 1,
                }
            },
            
            defaultStyle: {
                fontSize: parseInt(styles.tableFontSize) || 9,
                font: 'Roboto' // Roboto supports Cyrillic!
            }
        };
        
        // Generate and download PDF
        const fileName = `${config.exportFileName}_${new Date().toISOString().split('T')[0]}.pdf`;
        pdfMake.createPdf(docDefinition).download(fileName);

        return true;
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error;
    }
};

const DEFAULT_PAR_STYLES = {
    titleColor: '#28a745',
    titleFontSize: '10px',
    subtitleFontSize: '8px',
    subtitleColor: '#555555',
    tableFontSize: '7px',
    tableCellBorderColor: '#dee2e6',
    tableRowEvenBgColor: '#f1f8f4',
    tableRowOddBgColor: '#ffffff',
    footerFontSize: '6px',
    footerColor: '#666666',
    segmentCellWidth: 6,
    segmentBorderColor: '#333333',
};

/**
 * Builds the value cell for a row: plain text or segmented (one small cell per character).
 */
function buildParValueCell(value, useSegmented, styles) {
    const text = value != null && value !== '' ? String(value) : '';
    if (!useSegmented || text.length === 0) {
        return { text: text || ' ', fontSize: parseInt(styles.tableFontSize) || 7 };
    }
    const chars = text.split('');
    const segmentWidth = styles.segmentCellWidth ?? 6;
    const borderColor = styles.segmentBorderColor ?? '#333333';
    const fontSize = parseInt(styles.tableFontSize) || 7;
    return {
        table: {
            widths: chars.map(() => segmentWidth),
            body: [chars.map((c) => ({
                text: c,
                alignment: 'center',
                border: [true, true, true, true],
                fontSize,
            }))],
        },
        layout: {
            hLineColor: () => borderColor,
            vLineColor: () => borderColor,
        },
    };
}

/**
 * Normalizes API response to array of records (objects with fieldKey properties).
 */
function normalizeToRecords(data) {
    if (data == null) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.content)) return data.content;
    if (Array.isArray(data.data)) return data.data;
    if (typeof data === 'object' && !Array.isArray(data)) return [data];
    return [];
}

/** Tries record[key] and record[snake_case(key)] for API compatibility. */
function getRecordValue(record, fieldKey) {
    if (record == null || typeof record !== 'object' || !fieldKey) return undefined;
    if (record[fieldKey] !== undefined && record[fieldKey] !== null) return record[fieldKey];
    const snake = fieldKey.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
    return record[snake];
}

/**
 * Builds table body (array of rows) for one record.
 */
function buildParBodyForRecord(record, reportRows, styles) {
    return reportRows.map((row, rowIndex) => {
        let value;
        if (row.hardcoded != null) {
            value = row.hardcoded;
        } else if (row.fieldKey && record != null && typeof record === 'object') {
            const v = getRecordValue(record, row.fieldKey);
            value = v != null ? String(v) : '';
        } else {
            value = '';
        }
        const fontSize = parseInt(styles.tableFontSize) || 7;
        const labelCell = {
            text: row.label,
            fontSize,
            fillColor: rowIndex % 2 === 0 ? styles.tableRowEvenBgColor : styles.tableRowOddBgColor,
        };
        const valueCell = buildParValueCell(value, !!row.segmented, styles);
        const fill = rowIndex % 2 === 0 ? styles.tableRowEvenBgColor : styles.tableRowOddBgColor;
        const valueCellStyled = typeof valueCell === 'object' && valueCell.table
            ? { ...valueCell, fillColor: fill }
            : { ...valueCell, fillColor: fill };
        return [labelCell, valueCellStyled];
    });
}

/**
 * Generates PDF report for PAR (електронен трудов запис): two columns (label | value),
 * same visual style as existing reports, with optional segmented cells for values.
 * If response contains multiple records, each record is printed one under the other (same labels + data per record).
 * @param {Object|Array} data - Response from getEmpContractsParId: one record, or array of records, or { content: [...] }
 * @param {Array} reportRows - [{ label, fieldKey?, hardcoded?, segmented? }]
 * @param {Object} config - { title, exportFileName, customStyles? }
 */
export const generateParReportPDF = (data, reportRows, config = {}) => {
    const styles = { ...DEFAULT_PAR_STYLES, ...(config.customStyles || {}) };
    const currentDate = new Date().toLocaleDateString('bg-BG');
    const records = normalizeToRecords(data);

    const content = records.length === 0
        ? [{ text: 'Няма данни за показване.', margin: [0, 20, 0, 0] }]
        : records.map((record, index) => ({
            ...(index > 0 ? { pageBreak: 'before' } : {}),
            table: {
                widths: ['*', 'auto'],
                body: buildParBodyForRecord(record, reportRows, styles),
            },
            layout: {
                hLineColor: () => styles.tableCellBorderColor,
                vLineColor: () => styles.tableCellBorderColor,
            },
            margin: [0, 0, 0, 12],
        }));

    const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [30, 50, 30, 40],
        header: {
            margin: [30, 12, 30, 0],
            columns: [
                {
                    stack: [
                        ...(config.appendixRef != null && config.appendixRef !== ''
                            ? [{ text: config.appendixRef, fontSize: parseInt(styles.tableFontSize) || 7, color: styles.subtitleColor || '#555555', margin: [0, 0, 0, 2] }]
                            : []),
                        { text: config.title || 'Електронен трудов запис за сключване, изменение и прекратяване на трудов договор и за възникване, промяна и прекратяване на служебно правоотношение', bold: true, color: styles.titleColor, fontSize: parseInt(styles.titleFontSize) || 10 },
                        config.companyName ? { text: config.companyName, fontSize: parseInt(styles.subtitleFontSize) || 8, color: styles.subtitleColor } : {},
                    ].filter(Boolean),
                },
            ],
        },
        footer: (currentPage, pageCount) => ({
            margin: [30, 6, 30, 6],
            columns: [
                { text: `Генерирано на: ${currentDate}`, fontSize: parseInt(styles.footerFontSize) || 6, color: styles.footerColor },
                { text: `Страница ${currentPage} от ${pageCount}`, alignment: 'center', fontSize: parseInt(styles.footerFontSize) || 6, color: styles.footerColor },
            ],
        }),
        content,
        defaultStyle: {
            font: 'Roboto',
            fontSize: parseInt(styles.tableFontSize) || 7,
        },
    };

    const fileName = `${config.exportFileName || 'par_report'}_${new Date().toISOString().split('T')[0]}.pdf`;
    const pdfDoc = pdfMake.createPdf(docDefinition);
    pdfDoc.download(fileName);
    pdfDoc.getBlob((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
};

// Dossier PDF — see `src/utils/pdf/dossierPdf.js`
export {
    generateDossierPreviewPdf,
    buildDossierSectionTable,
    DOSSIER_LOGO_PATH,
    DOSSIER_PERSONAL_DATA_FIELD_KEYS,
} from './pdf/dossierPdf';
